import { FC, useEffect, useMemo, useState } from 'react';
import {
    ButtonLink,
    Dropdown,
    EmptyState,
    EmptyStateIcons,
    EmptyStateProps,
    Icons,
    List,
    ListItem,
    Search
} from '@silverfort/sf-components';

import { ENTITY_TYPE_TO_ICON, ENTITY_TYPES, FIELD_OPTIONS } from './consts';
import { Entity, useLazySearchEntities } from '../../../../api';
import SelectedEntityItem from './SelectedEntityItem';

export interface EntitiesSearchProps {
    selectedEntities: Entity[];
    onSelectedEntitiesChange: (entities: Entity[]) => void;
    enableSelectAll?: boolean;
    enableClearAll?: boolean;
    showCheckboxes?: boolean;
    disableSelection?: boolean;
    emptyStateProps?: EmptyStateProps;
}

const EntitiesSearch: FC<EntitiesSearchProps> = ({
    selectedEntities,
    onSelectedEntitiesChange,
    emptyStateProps,
    enableSelectAll = true,
    enableClearAll = true,
    showCheckboxes = true,
    disableSelection = false
}) => {
    const [entityTypes, setEntityTypes] = useState<number[] | undefined>();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [displayedField, setDisplayedField] = useState<string>(FIELD_OPTIONS[0].value!);
    const [allSelected, setAllSelected] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState(false);
    const { data: searchResults, isLoading, searchEntities } = useLazySearchEntities();

    const onSearch = (newSearchedQuery: string) => {
        setIsDirty(true);
        setSearchQuery(newSearchedQuery);
        searchEntities({ searchQuery: newSearchedQuery, entitiesTypes: entityTypes });
    };

    const onEntityTypeChange = (types?: number | number[]) => {
        setEntityTypes((types ?? []) as number[]);
    };

    const onDisplayFieldChange = (newDisplayField?: string | string[]) => {
        setDisplayedField(newDisplayField as string);
    };

    const onEntitySelected = (toggledEntity?: Entity) => {
        setIsDirty(true);
        if (!toggledEntity) return;
        if (selectedEntities.some((entity) => entity.guid === toggledEntity.guid)) {
            onSelectedEntitiesChange(
                selectedEntities.filter((previousSelected) => previousSelected.guid !== toggledEntity.guid)
            );
        } else {
            onSelectedEntitiesChange([...selectedEntities, toggledEntity]);
        }
    };

    const toggleSelectAll = () => {
        allSelected
            ? onSelectedEntitiesChange(
                  selectedEntities.filter((entity) => !searchResults.some((e) => e.guid === entity.guid))
              )
            : onSelectedEntitiesChange([
                  ...selectedEntities,
                  ...searchResults.filter((entity) => !selectedEntities.some((e) => e.guid === entity.guid))
              ]);
    };

    const handleClearAll = () => {
        setIsDirty(true);
        onSelectedEntitiesChange([]);
    };

    const entityGuidsDependency = useMemo(
        () =>
            selectedEntities
                .map((e) => e.guid)
                .sort()
                .join(','),
        [selectedEntities]
    );

    const dataGuidsDependency = useMemo(() => {
        return searchResults
            .map((e) => e.guid)
            .sort()
            .join(',');
    }, [searchResults]);

    useEffect(() => {
        const isAllSelected =
            searchResults.length &&
            searchResults
                .map(({ guid }) => guid)
                .every((guid) => selectedEntities.some((entity) => entity.guid === guid));
        setAllSelected(!!isAllSelected);
    }, [dataGuidsDependency, entityGuidsDependency, searchResults, selectedEntities]);

    const isEmptyState = !isDirty && !selectedEntities.length;

    return (
        <div className="h-full w-full flex flex-col">
            <div className="w-full h-14 flex justify-start items-center gap-2">
                <Dropdown
                    icon={Icons.ListFilter}
                    variant="filter"
                    selectedItems={entityTypes}
                    text="Type"
                    title="Type"
                    items={ENTITY_TYPES}
                    onChange={onEntityTypeChange}
                    multiple
                />
                <Dropdown
                    variant="trigger"
                    selectedItems={displayedField}
                    items={FIELD_OPTIONS}
                    onChange={onDisplayFieldChange}
                    size="small"
                    className="!w-[128px]"
                />
            </div>
            <div className="w-full flex-grow flex flex-col">
                <Search
                    onKeyDown={(e) => {
                        const isEmptyList = !searchQuery.length;
                        if (searchQuery[0]?.startsWith(e.key) || searchQuery[0]?.startsWith(e.key.toUpperCase())) {
                            e.stopPropagation();
                        }
                        if (isEmptyList && e.key.toLowerCase() === 'n') {
                            e.stopPropagation();
                        }
                    }}
                    placeholder="Search users, groups and OUs"
                    className="!h-[32px]"
                    border={true}
                    width="100%"
                    onChange={onSearch}
                />
                {isEmptyState ? (
                    <EmptyState
                        className="w-[100%] h-[100%]"
                        title="Add users"
                        description="Search and select users to enroll"
                        icon={EmptyStateIcons.NoItems}
                        {...emptyStateProps}
                    />
                ) : (
                    <div className="w-full max-h-[288px] flex flex-1 py-2 items-center gap-2">
                        <List className="w-full h-full flex-grow border rounded-lg px-0 pt-0">
                            <div className="w-full border-b py-3 px-4 h-10 flex items-center">
                                {enableSelectAll ? (
                                    <ListItem
                                        key="select_all"
                                        title="Select All"
                                        onCheck={toggleSelectAll}
                                        onClick={toggleSelectAll}
                                        multiSelect
                                        selected={allSelected}
                                        className="hover:bg-transparent [&]:p-0"
                                        titleClassName="leading-[16px]"
                                    />
                                ) : (
                                    <span className="text-13 font-sf">Select</span>
                                )}
                            </div>
                            <div className="w-full overflow-y-auto p-2">
                                {!isLoading &&
                                    searchResults?.map((entity) => (
                                        <ListItem
                                            key={entity.guid}
                                            value={entity}
                                            disabled={disableSelection}
                                            IconLeft={ENTITY_TYPE_TO_ICON[entity.type] ?? Icons.Add}
                                            title={entity[displayedField]}
                                            subtitle={`(${entity.domainName})`}
                                            onCheck={showCheckboxes ? () => onEntitySelected(entity) : undefined}
                                            onClick={() => onEntitySelected(entity)}
                                            multiSelect={showCheckboxes}
                                            selected={selectedEntities.some(
                                                (selectedEntity) => selectedEntity.guid === entity.guid
                                            )}
                                        />
                                    ))}
                            </div>
                        </List>
                        <List className="w-full h-full flex-grow border rounded-lg px-0 pt-0">
                            <div className="w-full border-b h-10 flex items-center justify-between py-3 px-4">
                                <p className="m-0 truncate text-13 font-sf h-4">
                                    {enableSelectAll && selectedEntities.length
                                        ? `${selectedEntities.length} Selected`
                                        : 'Selected'}
                                </p>
                                {enableClearAll && (
                                    <ButtonLink
                                        className="h-4 text-13 font-normal leading-[16px]"
                                        text="Clear All"
                                        underline
                                        onClick={handleClearAll}
                                    />
                                )}
                            </div>
                            <div className="w-full overflow-y-auto p-2">
                                {selectedEntities?.map((entity) => (
                                    <SelectedEntityItem
                                        key={entity.guid}
                                        entity={entity}
                                        displayedField={displayedField}
                                        onRemove={() => onEntitySelected(entity)}
                                    />
                                ))}
                            </div>
                        </List>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EntitiesSearch;
